import { Close } from '@mui/icons-material';
import { LoadingButtonProps } from '@mui/lab';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentProps,
  DialogProps,
  DialogTitle,
  IconButton,
  Box,
} from '@mui/material';
import { FC, ReactNode } from 'react';
import Button from '../Button';

type TModalProps = {
  open: boolean;
  handleClose: () => void;
  title?: ReactNode;
  hasCloseIcon?: boolean;
  children: ReactNode;
  hideActions?: boolean;
  hideCancelButton?: boolean;
  submitButtonProps?: LoadingButtonProps & {
    text?: string;
  };
  contentProps?: DialogContentProps;
  fullWidth?: DialogProps['fullWidth'];
  maxWidth?: DialogProps['maxWidth'];
  dialogProps?: Omit<DialogProps, 'open' | 'onClose' | 'fullWidth' | 'maxWidth'>;
};

const Modal: FC<TModalProps> = ({
  open,
  handleClose,
  title,
  hasCloseIcon = true,
  children,
  hideActions,
  hideCancelButton,
  submitButtonProps,
  contentProps,
  dialogProps,
  fullWidth = true,
  maxWidth = 'sm',
}) => {
  return (
    <Dialog
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      open={open}
      onClose={handleClose}
      {...dialogProps}
    >
      {(title || hasCloseIcon) && (
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: hasCloseIcon ? 1.5 : 3,
          }}
        >
          <Box component="span">{title}</Box>
          {hasCloseIcon && (
            <IconButton
              onClick={handleClose}
              size="small"
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          )}
        </DialogTitle>
      )}
      <DialogContent {...contentProps}>{children}</DialogContent>
      {!hideActions && (
        <DialogActions>
          {!hideCancelButton && (
            <Button
              variant="text"
              onClick={handleClose}
              sx={{ color: 'text.secondary' }}
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            variant="contained"
            color="primary"
            {...submitButtonProps}
          >
            {submitButtonProps?.text || 'Ok'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default Modal;
